import React, { useEffect, useState, useCallback } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    Grid,
    IconButton,
    Dialog,
    TextField,
    Toolbar,
    Tooltip,
    Typography,
} from "@mui/material";

import {
    DocumentLineProps,
    InventoryJournalLineViewModel,
    ListQuery,
} from "../../Types/ExportTypes";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import LoadingSection from "../LoadingSection";
import LocalizedNumberInputs from "../../components/Input/LocalizedNumberInputs";
import { ItemAutoComplete } from "../../components/AutoComplete/AutoCompletes";
import ImageBox from "../../components/Image/ImageBox";
import { formatNumber } from "../../Utils/NumberUtils";
import { generateInventoryJournalLineVariantLabel } from "../../Utils/VariantUtils";
import { useTranslation } from "react-i18next";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import ResponsivePagination from "../ResponsivePagination";

const InventoryJournalLines: React.FC<DocumentLineProps> = ({
    documentNo,
    documentType,
    history,
    reloadLines,
    locationCode,
}) => {
    const { authService } = useAuth();
    const { showSnackbar } = useSnackbar();
    const { showLoading, hideLoading } = useLoading();
    const { t } = useTranslation();

    const [query, setQuery] = useState<ListQuery>({ page: 0, pageSize: 20, filter: "" });
    const [lines, setLines] = useState<InventoryJournalLineViewModel[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLinesLoading, setIsLinesLoading] = useState(false);
    const [filter, setFilter] = useState(""
    );
    const [listMode, setListMode] = useState(true);
    const [editingLine, setEditingLine] = useState<InventoryJournalLineViewModel | null>(null);

    const fetchLines = useCallback(async () => {
        showLoading();
        setIsLinesLoading(true);
        try {
            const response = await authService.post<{ items: InventoryJournalLineViewModel[]; count: number }>(
                `inventoryApi/GetInventoryLines?documentNo=${documentNo}&documentType=${documentType}&open=${history === false}`,
                query
            );
            setLines(response.items);
            setTotalCount(response.count);
        } catch (error: any) {
            showSnackbar(`${t("dialog.fetchLinesError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
            setIsLinesLoading(false);
        }
    }, [authService, documentNo, documentType, history, query, showLoading, hideLoading, showSnackbar, t]);

    useEffect(() => {
        fetchLines();
    }, [fetchLines, query]);

    useEffect(() => {
        if (reloadLines === true) {
            setQuery({ ...query, filter, page: 0 });
        }
    }, [reloadLines]);

    const toggleListMode = () => {
        const newMode = !listMode;
        setListMode(newMode);
        setQuery({ ...query, page: 0, pageSize: newMode ? 20 : 2 });
    };

    const handleEditRow = (line: InventoryJournalLineViewModel) => {
        setEditingLine(line);
    };

    const handleFieldChange = (
        field: keyof InventoryJournalLineViewModel,
        value: any
    ) => {
        setEditingLine((prev) => {
            if (!prev) return prev;

            let updatedLine: InventoryJournalLineViewModel = {
                ...prev,
                [field]: value,
                hasChanges: true,
            };

            if (
                documentType == 4 &&
                field === "physicalQty" &&
                typeof prev.estimatedQty === "number"
            ) {
                updatedLine.adjustment = (value ?? 0) - (prev.estimatedQty ?? 0);
            }

            return updatedLine;
        });
    };

    const handleSaveLine = async () => {
        if (!editingLine) return;
        if (history) return;
        if (!locationCode || locationCode.length === 0) {
            showSnackbar(t("dialog.missingLocation"), "error");
            return;
        }
        try {
            showLoading();
            setIsLinesLoading(true);
            await authService.post(`inventoryApi/CreateOrUpdateLine`, editingLine);
            setLines((prev) =>
                prev.map((line) =>
                    line.id === editingLine.id ? { ...editingLine, hasChanges: false } : line
                )
            );
            showSnackbar(t("dialog.lineSaved"), "success");
            setEditingLine(null);
        } catch (error: any) {
            console.error("Failed to update row:", error);
            showSnackbar(`${t("dialog.lineSaveError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
            setIsLinesLoading(false);
        }
    };

    const handleRowDelete = async (line: InventoryJournalLineViewModel) => {
        const isConfirmed = window.confirm(t("dialog.deleteRowConfirm"));
        if (!isConfirmed) return;
        showLoading();
        setIsLinesLoading(true);
        try {
            await authService.post("inventoryApi/DeleteLine", line);
            setLines((prev) => prev.filter((l) => l.id !== line.id));
            showSnackbar(t("dialog.rowDeleted"), "success");
        } catch (error: any) {
            console.error("Failed to delete row:", error);
            showSnackbar(`${t("dialog.rowDeleteError")} : ${error.response?.data ?? error.message}`, "error");
        } finally {
            hideLoading();
            setIsLinesLoading(false);
        }
        setQuery((prev) => ({ ...prev, page: 0, totalCount: totalCount - 1 }));
    };

    const handleSearchSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        setQuery({ ...query, filter, page: 0 });
    };

    return (
        <Box sx={{ padding: "1rem" }}>
            <Box
                component="form"
                onSubmit={handleSearchSubmit}
                sx={{ display: "flex", gap: 2, marginBottom: 2 }}
            >
                <FormControl fullWidth>
                    <ItemAutoComplete
                        label={t("common.searchForItem")}
                        onChange={(selected) => setFilter(selected?.value || "")}
                        infoText={t("help.journalItemNoMultipleAddItem")}
                    />
                </FormControl>
                <Button variant="contained" color="primary" type="submit">
                    {t("common.search")}
                </Button>
            </Box>

            <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={toggleListMode}
            >
                {t("common.toggleListMode")}
            </Button>

            <Box sx={{ padding: "1rem" }}>
                <LoadingSection loading={isLinesLoading}>
                    {listMode ? (
                        <Grid container spacing={1}>
                            {lines.map((line, index) => (
                                <Grid item xs={12} key={line.id}>
                                    <Grid
                                        container
                                        spacing={1}
                                        sx={{
                                            backgroundColor:
                                                index % 2 === 0 ? "transparent" : "#fafafa",
                                            ":hover": {
                                                backgroundColor:
                                                    index % 2 === 0 ? "#f0f0f0" : "#e8e8e8",
                                            },
                                        }}
                                    >
                                        <Grid item xs={12} lg={ 3} sx={{ display: "flex", justifyContent: "center" }}>
                                            <ImageBox
                                                imageUrl={line.img_Url ?? ""}
                                                altText={line.description || "No Description"}
                                                fallbackText={t("common.noImage")}
                                                width="80px"
                                                height="80px"
                                            />
                                        </Grid>
                                        <Grid item xs={12} lg={4}>
                                            <Typography variant="body2" sx={{ color: "gray" }}>
                                                {line.no}
                                            </Typography>
                                            <Typography variant="body1">
                                                {line.description}
                                            </Typography>
                                            <Typography variant="body2">
                                                {generateInventoryJournalLineVariantLabel(line)}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: "gray" }} >
                                                {line.barcode}
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12} lg={3}>
                                            {documentType == 4 && (
                                                <Typography variant="subtitle2" sx={{ color: "gray" }}>
                                                    {t("common.estimatedQty")}: {formatNumber(line.physicalQty)}
                                                </Typography>
                                            )}
                                            {documentType != 3 && documentType != 2 && (
                                                <Typography variant="body2" sx={{ color: "gray" }}>
                                                    {t("common.qty")}: {formatNumber(documentType != 0 ? line.physicalQty : line.estimatedQty)}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" sx={{ color: "gray" }}>
                                                {(documentType == 3 || documentType == 2) ? t("common.qty") : t("common.adjustment")}: {formatNumber(line.adjustment)}
                                            </Typography>

                                        </Grid>
                                        {!history && (
                                            <>
                                            <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <Tooltip title={t("common.edit")}
                                                >
                                                    <IconButton color="secondary" onClick={() => handleEditRow(line)}>
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                  </Grid>
                                                <Grid item xs={12} lg={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
                                                <Tooltip title={t("common.delete")}
                                                >
                                                    <IconButton color="error" onClick={async () => { await handleRowDelete(line); }}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </Tooltip>
                                                </Grid>
                                            </>
                                        )}
                                    </Grid>
                                </Grid>
                            ))}
                        </Grid>
                    ) : (
                        <Grid container spacing={2} sx={{ mb: 2 }} justifyContent="center" alignItems="center">
                            {lines.map((line) => (
                                <Grid
                                    item
                                    sm={lines.length === 1 ? 12 : 6}
                                    md={lines.length === 1 ? 12 : 6}
                                    key={line.id}
                                >
                                    <Card variant="outlined">
                                        {!history && (
                                            <Toolbar sx={{ backgroundColor: "primary.light", color: "white" }}>
                                                <Grid container spacing={1}>
                                                    <Grid item xs={4}>
                                                        <Typography variant="h6" fontWeight="bold">
                                                            {line.lineNo}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Tooltip title={t("common.edit")}>
                                                            <IconButton color="secondary" onClick={() => handleEditRow(line)}>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <Tooltip title={t("common.delete")}>
                                                            <IconButton color="error" onClick={async () => { await handleRowDelete(line); }}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Grid>
                                                </Grid>
                                            </Toolbar>
                                        )}
                                        <CardContent>
                                            <Grid container spacing={1}>
                                                <Grid item xs={12} sx={{ marginBottom: "1rem" }}>
                                                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                                                        <ImageBox
                                                            imageUrl={line.img_Url ?? ""}
                                                            altText={line.description || "No Description"}
                                                            fallbackText={t("common.noImage")}
                                                            width="120px"
                                                            height="120px"
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                                        {line.no}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography sx={{ color: "gray" }}>{line.description}</Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Typography variant="body2" sx={{ color: "gray" }}>
                                                        {generateInventoryJournalLineVariantLabel(line)}
                                                    </Typography>
                                                </Grid>
                                                {line.barcode && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2" sx={{ color: "gray" }}>
                                                            {t("common.barcode")}: {line.barcode}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                {documentType != 3 && documentType != 2 && (
                                                    <Grid item xs={12}>
                                                        <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                                            {t("common.qty")}: {formatNumber(documentType !== 0 ? line.estimatedQty : line.physicalQty)}
                                                        </Typography>
                                                    </Grid>
                                                )}
                                                <Grid item xs={12}>
                                                    <Typography variant="body2">
                                                        {(documentType == 3 || documentType == 2) ? t("common.qty") : t("common.adjustment")}: {formatNumber(line.adjustment)}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs={12}>
                                                    <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                                                        <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                                            {t("common.costAmount")}: {formatNumber(line.costAmountPerUnit)}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </LoadingSection>
            </Box>

            <ResponsivePagination
                page={query.page}
                pageSize={query.pageSize}
                totalCount={totalCount}
                onPageChange={(newPage) => setQuery({ ...query, page: newPage })}
                sx={{ marginTop: "1rem" }}
            />

            <Dialog
                open={!!editingLine}
                onClose={() => setEditingLine(null)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: "8px", maxHeight: "80vh", overflowY: "auto" } }}
            >
                <Box sx={{ p: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                    <Typography variant="h6">{t("common.editLine")}</Typography>

                    {editingLine && (
                        <Grid container spacing={1}>
                            <Grid item xs={12} sx={{ textAlign: "center", marginBottom: "1rem" }}>
                                {editingLine.img_Url ? (
                                    <Box
                                        component="img"
                                        src={editingLine.img_Url}
                                        alt={editingLine.description || "No Description"}
                                        sx={{ width: "100%", maxWidth: "120px", maxHeight: "120px", height: "auto", borderRadius: "8px" }}
                                    />
                                ) : (
                                    <Typography align="center" sx={{ color: "gray" }}>{t("common.noImage")}</Typography>
                                )}
                            </Grid>
                            <Grid item xs={12}>
                                <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                                    {editingLine.no} - {editingLine.description}
                                </Typography>
                            </Grid>
                            {editingLine.variantId && (
                                <Grid item xs={12}>
                                    <Typography variant="body2" sx={{ color: "gray" }}>
                                        {generateInventoryJournalLineVariantLabel(editingLine)}
                                    </Typography>
                                </Grid>
                            )}
                            {editingLine.barcode && (
                                <Grid item xs={12}>
                                    <TextField
                                        label={t("common.barcode")}
                                        variant="outlined"
                                        fullWidth
                                        value={editingLine.barcode}
                                        disabled
                                    />
                                </Grid>
                            )}

                            {documentType != 3 && documentType != 2 && (
                                <Grid item xs={12}>
                                    <LocalizedNumberInputs
                                        label={documentType == 0 ? t("common.qty") : t("common.estimatedQty")}
                                        value={editingLine.estimatedQty ?? 1}
                                        onValueChange={() => { }}
                                        disabled
                                        required
                                        error={editingLine.estimatedQty == null}
                                        helperText={editingLine.estimatedQty == null ? t("help.estimatedQtyRequired") : ""}
                                        allowNegative={true}
                                        decimalScale={4}
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>
                            )}
                            {documentType != 0 && documentType != 3 && documentType != 2 && (
                                <Grid item xs={12}>
                                    <LocalizedNumberInputs
                                        label={t("common.physicalQty")}
                                        value={editingLine.physicalQty ?? 1}
                                        onValueChange={(value) => handleFieldChange("physicalQty", value ?? null)}
                                        required
                                        disabled={documentType != 4 || history}
                                        error={editingLine.physicalQty == null}
                                        helperText={editingLine.physicalQty == null ? t("help.physicalQtyRequired") : ""}
                                        allowNegative={true}
                                        decimalScale={4}
                                        thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                        decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                    />
                                </Grid>

                            )}
                            <Grid item xs={12}>
                                <LocalizedNumberInputs
                                    label={(documentType == 3 || documentType == 2) ? t("common.qty") : t("common.adjustment")}
                                    key={editingLine.adjustment}
                                    value={editingLine.adjustment ?? 0}
                                    onValueChange={(value) => handleFieldChange("adjustment", value ?? null)}
                                    required
                                    disabled={(documentType != 0 && documentType != 3 && documentType != 2) || history}
                                    error={editingLine.adjustment == null}
                                    helperText={editingLine.adjustment == null ? t("help.adjustmentRequired") : ""}
                                    allowNegative={true}
                                    decimalScale={4}
                                    thousandSeparator={Intl.NumberFormat(navigator.language).formatToParts(1000).find(part => part.type === "group")?.value || ","}
                                    decimalSeparator={Intl.NumberFormat(navigator.language).formatToParts(1.1).find(part => part.type === "decimal")?.value || "."}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <Box sx={{ backgroundColor: "lightgreen", padding: "0.5rem", borderRadius: "4px", textAlign: "center" }}>
                                    <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                                        {t("common.costAmount")}: {formatNumber(editingLine.costAmountPerUnit)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    )}

                    <Box sx={{ display: "flex", justifyContent: "space-between", mt: 2 }}>
                        <Button onClick={handleSaveLine} variant="contained" startIcon={<SaveIcon />} disabled={!editingLine?.hasChanges || history}>
                            {t("common.save")}
                        </Button>
                        <Button onClick={() => setEditingLine(null)} variant="outlined" startIcon={<CancelIcon />}>
                            {t("common.cancel")}
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </Box>
    );
};

export default InventoryJournalLines;